import React, { createContext, useContext, useState } from 'react';
import './DrawerContext.scss';
import {
  Drawer,
  Box,
  Divider,
  Typography,
  IconButton,
  Container,
  ThemeProvider,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FormikForm from '../Utility/FormikForm';
import { getAppTheme } from "../../services/authContext";

const DrawerContext = createContext();

export const useDrawerContext = () => useContext(DrawerContext);

export const DrawerProvider = ({ children }) => {
  const [drawerStack, setDrawerStack] = useState([]);
  const mode = useSelector((state) => state.theme.mode)
  const theme = getAppTheme(mode);

  const showDrawerForm = (options = {}) => {
    return new Promise((resolve) => {
      const drawerId = Date.now() + Math.random(); // Unique ID for each drawer
      
      const newDrawer = {
        id: drawerId,
        isOpen: true,
        title: options.title || 'Form',
        formFields: options.formFields || options.useFormik || [],
        initialValues: options.initialValues || {},
        validationSchema: options.validationSchema || {},
        submitButtonText: options.submitButtonText || 'Submit',
        content: options.content || null,
        hideForm: options.hideForm || false,
        fullWidth: options.fullWidth || false,
        customWidth: options.customWidth || null,
        contentPadding: options.contentPadding !== undefined ? options.contentPadding : 3,
        updateContent: options.updateContent || null,
        onSubmit: (values) => {
          if (options.onSubmit) {
            options.onSubmit(values);
          } else if (options.onConfirm) {
            options.onConfirm(values);
          }
          closeDrawer(drawerId);
          resolve(values);
        },
        onClose: () => {
          closeDrawer(drawerId);
          if (options.onClose) {
            options.onClose();
          } else if (options.onCancel) {
            options.onCancel();
          }
          resolve(false);
        },
      };

      setDrawerStack(prev => [...prev, newDrawer]);
    });
  };

  const closeDrawer = (drawerId) => {
    setDrawerStack(prev => prev.filter(drawer => drawer.id !== drawerId));
  };

  const updateDrawerContent = (newContent) => {
    setDrawerStack(prev => {
      const updated = [...prev];
      if (updated.length > 0) {
        updated[updated.length - 1].content = newContent;
      }
      return updated;
    });
  };

  const getDrawerWidth = (drawer) => {
    if (drawer.fullWidth) return "100%";
    if (drawer.customWidth) return drawer.customWidth;
    return "100%";
  };

  return (
    <ThemeProvider theme={theme}>
      <DrawerContext.Provider value={{ showDrawerForm, updateDrawerContent }}>
        {children}
        {drawerStack.map((drawer, index) => (
          <Drawer
            key={drawer.id}
            anchor="right"
            open={drawer.isOpen}
            onClose={() => drawer.onClose()}
            sx={{
              "& .MuiDrawer-paper": {
                width: getDrawerWidth(drawer),
                maxWidth: "100%",
                overflow: "hidden",
                zIndex: 1300 + index * 10, // Ensure proper stacking
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: 2,
                backgroundColor: '#5648b5',
                color: 'white',
                position: 'sticky',
                top: 0,
                zIndex: 10,
              }}
            >
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => drawer.onClose()}
                aria-label="close"
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
                {drawer.title}
              </Typography>
              <IconButton 
                color="inherit" 
                onClick={() => drawer.onClose()} 
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />

            <Container
              sx={{
                p: drawer.contentPadding,
                overflow: 'auto',
                height: 'calc(100% - 64px)',
                display: 'flex',
                flexDirection: 'column'
              }}
              maxWidth={false}
            >
              {drawer.content ? (
                drawer.content
              ) : !drawer.hideForm ? (
                <FormikForm
                  initialValues={drawer.initialValues}
                  validationSchema={drawer.validationSchema}
                  fields={drawer.formFields}
                  submitButtonText={drawer.submitButtonText}
                  onSubmit={drawer.onSubmit}
                  isLoading={false}
                  enableReinitialize={true}
                />
              ) : null}
            </Container>
          </Drawer>
        ))}
      </DrawerContext.Provider>
    </ThemeProvider>
  );
};

export default DrawerProvider;